import { DryingProgressGraphOptions } from "@/utils/DryingProgressGraphDataExtractor";
import { WeatherInformationDetailsLevel } from "@/utils/DryingGraphYAxis";

/**
 * The only part of `TargetSensors` this module needs.
 *
 * Declared here rather than imported from `SelectionTable.vue` on purpose: a pure utils module
 * should not depend on a component, and a type-only import from a `.vue` file is also unresolvable
 * by the coverage collector. `TargetSensors` remains structurally assignable to this.
 */
export interface PhaseVisibility {
  showPhases: boolean;
}

/**
 * Decision logic for the drying-graph panel: the graph, its sensor legend, and the RENO time series.
 *
 * Extracted from `IresDeviceDetail.vue` so the same panel can be reused by the RENO review queue
 * without duplicating it. Pure functions only, no Vue and no Firestore, because this is the part
 * worth testing: a handful of inverted booleans and conditional arguments that decide whether the
 * graph draws limit lines, phase annotations or weather at all. Getting one wrong changes the chart
 * silently, with nothing failing.
 *
 * **This module is a faithful transcription of the existing behaviour, not a redesign.** Each
 * function cites the `IresDeviceDetail.vue` lines it came from so the two can be diffed by hand.
 * Every quirk below is intentional and preserved, including the ones that look like bugs.
 */

/**
 * Inputs to the graph-options builder, mirroring the component fields the original call read.
 *
 * Transcribed from `IresDeviceDetail.vue:1578-1587`.
 */
export interface DryingGraphOptionsInput {
  /** True when the selected chain is the device's currently running one. */
  isShowingActiveProgramChain: boolean;
  /** Refresh clock, used as the forced end date for an active chain so it grows over time. */
  lastRefreshDate: Date;
  /** Id of the chain the graph last rendered, used to suppress re-animation. */
  latestDisplayedProgramChainId: string;
  /** Id of the chain being rendered now. */
  programChainId: string;
  /** True while the compressed multi-chain overview is shown. */
  showingOverviewProgramChain: boolean;
  /** Sensor selection state, whose `showPhases` drives phase annotations. */
  targetSensors: PhaseVisibility;
  isAllowedToViewRunStartsCausedByMaxRunDuration: boolean;
}

/**
 * Builds the options object passed to `extractDryingProgressGraphData`.
 *
 * Note the two **inverted** flags: the component stores "show phases" and "may view run starts",
 * while the extractor takes "hide phase annotations" and "hide run starts". That inversion is the
 * single easiest thing to get backwards in this whole panel, which is why it is pinned by tests.
 *
 * `forcedEndDate` is only set for an active chain: a finished chain ends at its own last sample, and
 * forcing "now" would stretch the axis into empty space.
 *
 * @param input Component state at render time.
 * @returns The options object, byte-for-byte equivalent to the original inline literal.
 */
export function buildDryingGraphOptions(
  input: DryingGraphOptionsInput
): DryingProgressGraphOptions {
  return {
    showDetails: true,
    forcedEndDate: input.isShowingActiveProgramChain
      ? input.lastRefreshDate
      : undefined,
    skipAnimation: input.latestDisplayedProgramChainId === input.programChainId,
    hideLimitLines: input.showingOverviewProgramChain,
    hidePhaseAnnotations: !input.targetSensors.showPhases,
    hideRunStartsCausedBeMaxRunDuration:
      !input.isAllowedToViewRunStartsCausedByMaxRunDuration,
  };
}

/** Inputs to the weather/power argument resolution, from `IresDeviceDetail.vue:1589-1592`. */
export interface DryingGraphExtrasInput {
  showPowerLine: boolean;
  isAllowedToViewPowerInformation: boolean;
  isAllowedToViewWeatherInformation: boolean;
  latitude?: number | null;
  longitude?: number | null;
  weatherInformationDetailsLevel: WeatherInformationDetailsLevel;
}

/** The trailing arguments `extractDryingProgressGraphData` takes after the options object. */
export interface DryingGraphExtras {
  showPowerLine: boolean;
  /** Never null: the falsy branch yields undefined, matching what the extractor accepts. */
  latitude?: number;
  longitude?: number;
  weatherInformationDetailsLevel?: WeatherInformationDetailsLevel;
}

/**
 * Resolves the power and weather arguments, each gated on its own capability.
 *
 * Preserves one quirk deliberately: the original used `this.latitude ? this.latitude : undefined`,
 * so a coordinate of exactly **0 becomes undefined** — latitude 0 is a real place, but on the
 * equator, and reproducing the behaviour matters more than fixing it inside a refactor. Changing it
 * would be a behaviour change smuggled into a move, which is exactly what the tests here exist to
 * prevent. Fix it separately if it ever matters.
 *
 * @param input Component state at render time.
 * @returns The resolved trailing arguments.
 */
export function resolveDryingGraphExtras(
  input: DryingGraphExtrasInput
): DryingGraphExtras {
  return {
    showPowerLine: input.showPowerLine && input.isAllowedToViewPowerInformation,
    latitude:
      input.isAllowedToViewWeatherInformation && input.latitude
        ? input.latitude
        : undefined,
    longitude:
      input.isAllowedToViewWeatherInformation && input.longitude
        ? input.longitude
        : undefined,
    weatherInformationDetailsLevel: input.isAllowedToViewWeatherInformation
      ? input.weatherInformationDetailsLevel
      : undefined,
  };
}


/**
 * Whether weather coordinates need resolving before computing.
 *
 * From `IresDeviceDetail.vue:1556-1558`: only when weather is permitted and **neither** coordinate
 * is known. Note it is `!lat && !lng`, not `||`, so a half-known position does not trigger a lookup.
 *
 * Accepts `null` as well as `undefined` because the component stores `number | null`; both are falsy,
 * so the behaviour is identical either way.
 */
export function needsCoordinateLookup(
  isAllowedToViewWeatherInformation: boolean,
  latitude?: number | null,
  longitude?: number | null
): boolean {
  return isAllowedToViewWeatherInformation && !latitude && !longitude;
}

/**
 * The position already stored for an address, when it is one the weather can be asked for.
 *
 * Added 2026-08-24, and the one function here that is not a transcription: the device page used to
 * geocode the damage address, the branch or the company address on every opening, although IRES
 * Cloud writes a graded position onto all three (see `docs/components/location-picker.md`). Reading
 * it instead removes a billed Geocoding request per opening.
 *
 * Confidence is deliberately not consulted. Weather is km-scale data, so the coarsest band — the
 * centre of a locality — answers it as well as a rooftop does, and a hand-placed pin better than
 * either.
 *
 * @param coordinates - what is stored: `undefined` never looked up, `null` looked up without a
 *   result, otherwise a position.
 * @returns the position, or null when there is none to use and the address must be looked up.
 *   A stored `null` also returns null, so its address is looked up rather than left without
 *   weather: that tombstone is unreliable in production, because `updateClientData` in IRES Cloud
 *   writes one whenever a project is saved with no coordinates to hand.
 */
export function readStoredWeatherPosition(
  coordinates?: { latitude?: number | null; longitude?: number | null } | null
): { latitude: number; longitude: number } | null {
  // Field by field rather than a truthiness check on the object, so a record written before the
  // position carried its current shape falls through to the lookup instead of blanking the weather.
  if (
    typeof coordinates?.latitude !== "number"
    || typeof coordinates.longitude !== "number"
  ) {
    return null;
  }

  return { latitude: coordinates.latitude, longitude: coordinates.longitude };
}

/**
 * Whether a requested weather level must be abandoned for lack of coordinates.
 *
 * From `IresDeviceDetail.vue:1564-1571`. When this is true the caller resets the level to `NONE` and
 * shows the "weather unavailable" snackbar.
 */
export function mustAbandonWeather(
  weatherInformationDetailsLevel: WeatherInformationDetailsLevel,
  latitude?: number | null,
  longitude?: number | null
): boolean {
  return (
    weatherInformationDetailsLevel !== WeatherInformationDetailsLevel.NONE &&
    !latitude &&
    !longitude
  );
}

/** Inputs to the panel's visibility decisions, from `IresDeviceDetail.vue:547-581`. */
export interface DryingGraphVisibilityInput {
  isShowingComBoxDetails: boolean;
  isAllowedToViewAiPredictions: boolean;
  showRateDrying: boolean;
  showGraph: boolean;
  hasSelectedProgramChain: boolean;
  predictionShowTimeline: boolean;
  selectedAiModelVersion: string;
}

/**
 * Whether the AI predictions panel is shown. `IresDeviceDetail.vue:555`.
 *
 * Note it requires `showRateDrying`, i.e. the predictions live behind the same content toggle as the
 * expert rating form, whereas {@link showRenoTimeSeries} does not.
 */
export function showAiPredictions(input: DryingGraphVisibilityInput): boolean {
  return (
    input.isShowingComBoxDetails &&
    input.isAllowedToViewAiPredictions &&
    input.showRateDrying
  );
}

/**
 * Whether the RENO time series is shown alongside the graph. `IresDeviceDetail.vue:579`.
 *
 * Deliberately **not** gated on `showRateDrying`, unlike {@link showAiPredictions}: the time series
 * extends the graph's timeline, so it follows the graph rather than the rating form. Actual
 * visibility still depends on trend availability inside `RenoTimeSeries` itself.
 */
export function showRenoTimeSeries(input: DryingGraphVisibilityInput): boolean {
  return (
    input.isShowingComBoxDetails &&
    input.isAllowedToViewAiPredictions &&
    !!input.showGraph
  );
}

/** Whether the Stationär / Trocknen timeline markers are shown. `IresDeviceDetail.vue:563`. */
export function showTimelineMarkers(
  input: DryingGraphVisibilityInput
): boolean {
  return (
    showAiPredictions(input) &&
    input.hasSelectedProgramChain &&
    !!input.showGraph &&
    input.predictionShowTimeline
  );
}

/**
 * Whether the selected prediction came from a RENO model. `IresDeviceDetail.vue:571`.
 *
 * A prefix test, not an equality test, so it covers R1.0, R2.0 and whatever comes next. Legacy seed
 * models do not start with "R" and get no time series.
 */
export function isRenoModelVersion(modelVersion: string): boolean {
  return modelVersion.startsWith("R");
}
